import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { BaasicFieldDropdown, BaasicDropdown, BaasicButton, BaasicInput, BasicInput, DatePicker, DatePickerField, BaasicModal, BaasicDraggableModal } from 'core/components';
import { NikoNewPatientTemplateCreate } from 'application/niko-patient/components';
import { NikoFacilityOrderConfirmationModal } from 'application/niko-facility-order/components';
import { Stepper } from "@progress/kendo-react-layout";
import { observer } from "mobx-react";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Button } from '@progress/kendo-react-buttons';

const ExistingPatientList = observer(({ store }) => {
    const {
        existingPatients,
        selectedExistingPatient,
        selectExistingPatient,
        resolvingScenario,
        blockingMessage,
    } = store;

    if (blockingMessage) {
        return (
            <div className="message message--error is-round u-mar--bottom--med">
                <i className="u-icon u-icon--med u-icon--warning-triangle--red"></i>
                <div>
                    Error! {blockingMessage}
                </div>
            </div>
        );
    }

    return (
        <div className="u-mar--top--med">
            <div className="u-mar--bottom--sml">
                <div className="message message--note is-round u-mar--bottom--med">
                    <i className="u-icon u-icon--med u-icon--warning-circle--peach"></i>
                    <div>
                        Existing patients found. Please select below to update existing patient.
                    </div>
                </div>
                <div className="card--primary__body">
                    {existingPatients.map((patient, key) => {
                        let status = 'is-active';
                        if (patient.status === "Hospital"){
                            status = 'is-hospital';
                        }
                        else if (patient.status === "Discharged"){
                            status = 'is-discharged';
                        }

                        return (
                        <div key={patient.id} className='u-flex--default u-flex--gap--2 u-mar--bottom--med'>
                            <div>
                                <input
                                    type="radio"
                                    id={'patientName-' + patient.id}
                                    className='input--radio'
                                    checked={selectedExistingPatient && selectedExistingPatient.id === patient.id}
                                    onChange={() => selectExistingPatient(patient)}
                                />
                                <label htmlFor={'patientName-' + patient.id}>{patient.firstName} {patient.lastName}</label>
                            </div>
                        <div key={key} className="patient__group is-big">
                            <div className="u-flex--primary u-flex--no-wrap">
                                <i className="u-icon u-icon--med u-icon--patient-user u-mar--right--tny"></i>
                                <span>{patient.gender}</span>
                            </div>
                            <span className="patient__group__divider">|</span>
                            <div className="u-flex--primary u-flex--no-wrap">
                                <i className="u-icon u-icon--med u-icon--patient-camera u-mar--right--tny"></i>
                                <span>{moment(patient.dateOfBirth).format("MM/DD/YYYY")}</span>
                            </div>
                            <span className="patient__group__divider">|</span>
                            <div className="u-flex--primary u-flex--no-wrap">
                                <i className="u-icon u-icon--med u-icon--patient-ssn u-mar--right--tny"></i>
                                <span>{patient.ssn}</span>
                            </div>
                            <span className="patient__group__divider">|</span>
                            <div className="u-flex--primary u-flex--no-wrap">
                                <i className="u-icon u-icon--med u-icon--new-facility--primary u-mar--right--tny"></i>
                                <span>{patient.facility && patient.facility.name}</span>
                            </div>
                        </div>
                        <div>
                            <div className={`niko__status__badge ${status}`}>{patient.status}</div>
                        </div>
                        </div>);
                    })}
                    {resolvingScenario && (
                        <div className="u-mar--top--sml">
                            <span className="type--sml type--color--opaque">Checking patient...</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

const PatientInfoStep = observer(({ store }) => {
    const { form, genderStore, showExistingPatientList, checkingExisting, ssnError, validateSsn } = store;
    return (
        <div>
            <h4 className="type--med type--wgt--medium u-mar--bottom--med">Patient Info</h4>
            <div className="row u-flex--align-end">
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <BasicInput field={form.$('firstName')} />
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <BasicInput field={form.$('lastName')} />
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <BasicInput field={form.$('nickname')} />
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <BaasicFieldDropdown field={form.$('gender')} store={genderStore} />
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <DatePickerField field={form.$('dateOfBirth')} customFormat={"MM/dd/yyyy"} formatPlaceholder={{ year: "yyyy", month: "mm", day: "dd" }} footer={false} />
                </div>
            </div>
            <div className="row u-flex--align-end">
                <div className="form__group col col-lrg-4 u-mar--bottom--sml">
                    <label className="form__group__label">Social Security Number{!form.$('noSsn').value && <span className="validation__mark">*</span>}</label>
                    {ssnError && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                            {ssnError}
                        </div>
                    )}
                    <input
                        className="input input--base input--text"
                        value={form.$('ssn').value || ''}
                        onChange={(e) => form.$('ssn').set('value', e.target.value.replace(/\D/g, ''))}
                        onBlur={() => validateSsn()}
                        disabled={form.$('ssn').disabled}
                        maxLength={9}
                        placeholder="Enter SSN"
                    />
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--med-lrg">
                    <label className="form__group__label--checkbox">
                        <input
                            type="checkbox"
                            id='noSsn'
                            className='input--check'
                            checked={form.$('noSsn').value || false}
                            onChange={(e) => form.$('noSsn').set('value', e.target.checked)}
                        />
                        <label htmlFor="noSsn">No SSN</label>
                    </label>
                </div>
            </div>
            {checkingExisting && (
                <div className="u-mar--top--sml">
                    <span className="type--sml type--color--opaque">Checking for existing patients...</span>
                </div>
            )}
            {showExistingPatientList && <ExistingPatientList store={store} />}
        </div>
    );
});

const InsurancePolicyRow = observer(({ policy, isPrimary, isSwap, action, onRemove, onInactivate, onCancel, onUpdateField, onSave, onEdit, errors }) => {
    const typeLabel = isPrimary ? "Primary" : "Secondary";
    const prefix = policy.isActive ? typeLabel : `Inactive ${typeLabel}`;
    const relationshipName = policy.relationshipStore.value ? policy.relationshipStore.value.name : "Self";
    const showInsuredPersonFields = relationshipName === "Spouse" || relationshipName === "Parent" || relationshipName === "Other";
    const showEmployerField = relationshipName === "Employer";

    if (!policy.isEditing) {
        return (
            <div className='card card--grey card--tny card--flex u-mar--bottom--med'>
                <div className='u-flex--default u-flex--space-between'>
                    <div className='u-flex--default u-flex--gap--2'>
                        <span className='type--color--primary type--wgt--medium'>{policy.insuranceStore.value ? policy.insuranceStore.value.name : '—'}</span>
                        {policy.policyNo && (
                            <React.Fragment>
                                <span className='type--color--primary'>{policy.policyNo}</span>
                            </React.Fragment>
                        )}
                        {(policy.startDate || policy.endDate) && (
                            <React.Fragment>
                                <span className='type--color--secondary'>
                                    {policy.startDate ? moment(policy.startDate).format("MM/DD/YYYY") : '—'}
                                </span>
                            </React.Fragment>
                        )}
                    </div>
                    <div className="u-flex--default u-flex--gap--2">
                        <BaasicButton className="btn btn--icon" icon="u-icon u-icon--us-edit u-icon--sml" onlyIcon={true} label="Edit" onClick={onEdit} />
                        {action === "inactivate"
                            ? <BaasicButton className="btn btn--icon" icon="u-icon u-icon--sml u-icon--danger--primary" onlyIcon={true} label="Inactivate" title="Inactivate" onClick={onInactivate} />
                            : <BaasicButton className="btn btn--icon" icon="u-icon u-icon--delete--brand u-icon--sml" onlyIcon={true} label="Delete" onClick={onRemove} />}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='card card--outlined--grey card--sml card--flex u-mar--bottom--med'>
            {isSwap && (
                <div className="u-mar--bottom--sml">
                    <div className="row u-flex--align-end">
                        <div className="form__group col col-lrg-4 u-mar--bottom--med">
                            <label className="form__group__label">Previous {prefix} Insurance End Date{!policy.oldEndDateUnavailable && <span className="validation__mark">*</span>}</label>
                            <DatePicker
                                value={policy.oldEndDate ? new Date(policy.oldEndDate) : null}
                                onChange={(value) => onUpdateField('oldEndDate', value)}
                                placeholder={`Select Date`}
                                disabled={policy.oldEndDateUnavailable}
                            />
                            {errors && errors.oldEndDate && (
                                <div className="validation__message">
                                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                    {errors.oldEndDate}
                                </div>
                            )}
                        </div>
                        <div className="form__group col col-lrg-4 u-mar--bottom--med-lrg">
                            <label className="form__group__label--checkbox">
                                <input
                                    type="checkbox"
                                    id={"oldEndDateUnavailable-" + policy.id}
                                    className="input--check"
                                    checked={policy.oldEndDateUnavailable || false}
                                    onChange={(e) => onUpdateField('oldEndDateUnavailable', e.target.checked)}
                                />
                                <label htmlFor={"oldEndDateUnavailable-" + policy.id}>End Date Unavailable</label>
                            </label>
                        </div>
                    </div>
                </div>
            )}
            <div className="row u-mar--bottom--sml">

                <div className="form__group col col-lrg-6 u-mar--bottom--med">
                    <label className="form__group__label">{prefix} Insurance Name<span className="validation__mark">*</span></label>
                    {errors && errors.insuranceName && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                            {errors.insuranceName}
                        </div>
                    )}
                    <BaasicDropdown store={policy.insuranceStore} placeholder={`Select Insurance Name`} />
                </div>
                <div className="form__group col col-lrg-6 u-mar--bottom--med">
                    <label className="form__group__label">{prefix} Insurance Policy Number<span className="validation__mark">*</span></label>
                    {errors && errors.policyNo && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                            {errors.policyNo}
                        </div>
                    )}
                    <BaasicInput
                        placeholder={`Enter Insurance Policy Number`}
                        value={policy.policyNo || ''}
                        onChange={(e) => onUpdateField('policyNo', e.target.value)}
                    />
                </div>

                <div className="form__group col col-lrg-4 u-mar--bottom--med">
                    <label className="form__group__label">{prefix} Insurance Start Date<span className="validation__mark">*</span></label>
                    <DatePicker
                        value={policy.startDate ? new Date(policy.startDate) : null}
                        onChange={(value) => onUpdateField('startDate', value)}
                        placeholder={`Select Date`}
                    />
                    {errors && errors.startDate && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                            {errors.startDate}
                        </div>
                    )}
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--med">
                    <label className="form__group__label">{prefix} Insurance End Date</label>
                    <DatePicker
                        value={policy.endDate ? new Date(policy.endDate) : null}
                        onChange={(value) => onUpdateField('endDate', value)}
                        placeholder={`Select Date`}
                    />
                    {errors && errors.endDate && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                            {errors.endDate}
                        </div>
                    )}
                </div>
                <div className="form__group col col-lrg-4 u-mar--bottom--med">
                    <label className="form__group__label">{prefix} Insurance Relationship</label>
                    <BaasicDropdown store={policy.relationshipStore} placeholder={`Self`} />
                </div>

                {showInsuredPersonFields && (
                    <React.Fragment>
                        <div className="form__group col col-lrg-6 u-mar--bottom--med">
                            <label className="form__group__label">Insured First Name<span className="validation__mark">*</span></label>
                            <BaasicInput
                                placeholder="Enter Insured First Name"
                                value={policy.insuredFirstName || ''}
                                onChange={(e) => onUpdateField('insuredFirstName', e.target.value)}
                            />
                            {policy.errors && policy.errors.insuredFirstName && (
                                <div className="validation__message">
                                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                    {policy.errors.insuredFirstName}
                                </div>
                            )}
                        </div>
                        <div className="form__group col col-lrg-6 u-mar--bottom--med">
                            <label className="form__group__label">Insured Last Name<span className="validation__mark">*</span></label>
                            <BaasicInput
                                placeholder="Enter Insured Last Name"
                                value={policy.insuredLastName || ''}
                                onChange={(e) => onUpdateField('insuredLastName', e.target.value)}
                            />
                            {policy.errors && policy.errors.insuredLastName && (
                                <div className="validation__message">
                                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                    {policy.errors.insuredLastName}
                                </div>
                            )}
                        </div>
                        <div className="form__group col col-lrg-6 u-mar--bottom--med">
                            <label className="form__group__label">Insured DOB<span className="validation__mark">*</span></label>
                            <DatePicker
                                value={policy.insuredDOB ? new Date(policy.insuredDOB) : null}
                                onChange={(value) => onUpdateField('insuredDOB', value)}
                                max={new Date()}
                                placeholder="Select Date"
                            />
                            {policy.errors && policy.errors.insuredDOB && (
                                <div className="validation__message">
                                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                    {policy.errors.insuredDOB}
                                </div>
                            )}
                        </div>
                        <div className="form__group col col-lrg-6 u-mar--bottom--med">
                            <label className="form__group__label">Insured Gender<span className="validation__mark">*</span></label>
                            <BaasicDropdown store={policy.genderStore} placeholder="Select Gender" />
                            {policy.errors && policy.errors.insuredGender && (
                                <div className="validation__message">
                                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                    {policy.errors.insuredGender}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                )}
                {showEmployerField && (
                    <div className="form__group col col-lrg-6 u-mar--bottom--med">
                        <label className="form__group__label">Employer Name<span className="validation__mark">*</span></label>
                        <BaasicInput
                            placeholder="Enter Employer Name"
                            value={policy.insuredLastName || ''}
                            onChange={(e) => onUpdateField('insuredLastName', e.target.value)}
                        />
                        {policy.errors && policy.errors.insuredLastName && (
                            <div className="validation__message">
                                <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>
                                {policy.errors.insuredLastName}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className='u-display--flex u-display--flex--gap--sml u-align--h--right'>
                <BaasicButton className="btn btn--med btn--light" label="Cancel" onClick={onCancel} />
                <BaasicButton className="btn btn--med btn--primary u-mar--left--sml" label="Save" onClick={onSave} />
            </div>
        </div>
    );
});

const InactivatePolicyModalContent = observer(({ store, modalParams }) => {
    const { type, index, policy } = modalParams.data || {};
    const [endDate, setEndDate] = React.useState(policy && policy.endDate ? new Date(policy.endDate) : null);
    const [endDateUnavailable, setEndDateUnavailable] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        setEndDate(policy && policy.endDate ? new Date(policy.endDate) : null);
        setEndDateUnavailable(false);
        setError(null);
    }, [modalParams.isOpen]);

    const onConfirm = () => {
        if (!endDateUnavailable && !endDate) {
            setError("The End Date field is required.");
            return;
        }
        if (!endDateUnavailable && policy && policy.startDate && endDate && new Date(endDate) <= new Date(policy.startDate)) {
            setError("End Date must be greater than Start Date.");
            return;
        }
        store.inactivatePolicy(type, index, endDate, endDateUnavailable);
    };

    return (
        <section>
            <div className="modal__body type--center">
                <i className="status--sqare is-big cross-circle u-mar--bottom--med u-radius--full" />
                <h3 className="modal__header__title u-mar--bottom--med">Inactivate Policy</h3>
                <p className="u-mar--bottom--med type--color--secondary">
                    Are you sure you want to inactivate this insurance policy?
                </p>
            </div>
            <div className="row u-flex--align-end">
                <div className="form__group col col-lrg-6 u-mar--bottom--sml">
                    <label className="form__group__label">End Date{!endDateUnavailable && <span className="validation__mark">*</span>}</label>
                    <DatePicker
                        value={endDate}
                        onChange={(val) => { setEndDate(val); setError(null); }}
                        placeholder="Select Date"
                        disabled={endDateUnavailable}
                    />
                    {error && (
                        <div className="validation__message">
                            <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>{error}
                        </div>
                    )}
                </div>
                <div className="form__group col col-lrg-6 u-mar--bottom--med-lrg">
                    <label className="form__group__label--checkbox">
                        <input
                            type="checkbox"
                            id="inactivate-endDateUnavailable"
                            className="input--check"
                            checked={endDateUnavailable}
                            onChange={(e) => { setEndDateUnavailable(e.target.checked); setEndDate(e.target.checked ? null : endDate); setError(null); }}
                        />
                        <label htmlFor="inactivate-endDateUnavailable">End Date Unavailable</label>
                    </label>
                </div>
            </div>
            <div className="modal__footer">
                <BaasicButton className="btn btn--med btn--light" label="Cancel" onClick={() => modalParams.close()} />
                <BaasicButton className="btn btn--med btn--primary" label="Confirm" onClick={onConfirm} />
            </div>
        </section>
    );
});

const InsuranceQuestionModalContent = ({ form, modalParams }) => {
    const [questionValue, setQuestionValue] = React.useState(form.$('insuranceQuestion').value || '');

    React.useEffect(() => {
        setQuestionValue(form.$('insuranceQuestion').value || '');
    }, [modalParams.isOpen]);

    return (
        <section>
            <div className="modal__header">
                <h3 className="modal__header__title">Insurance Question</h3>
            </div>
            <div className="modal__body">
                <div className="form__group">
                    <textarea
                        className="input input--med input--textarea"
                        placeholder="Enter Insurance Question"
                        value={questionValue}
                        onChange={(e) => setQuestionValue(e.target.value)}
                        rows={4}
                        autoFocus
                    />
                </div>
            </div>
            <div className="modal__footer">
                <BaasicButton
                    className="btn btn--med btn--light"
                    label="Resolve"
                    icon="u-icon u-icon--xmed u-icon--check"
                    onClick={() => { form.$('insuranceQuestion').set('value', ''); modalParams.close(); }}
                />
                <BaasicButton
                    className="btn btn--med btn--primary"
                    label="Save"
                    onClick={() => { form.$('insuranceQuestion').set('value', questionValue); modalParams.close(); }}
                />
            </div>
        </section>
    );
};

const InsuranceChangesNoteModalContent = ({ form, modalParams }) => {
    const [noteValue, setNoteValue] = React.useState(form.$('note').value || '');

    React.useEffect(() => {
        setNoteValue(form.$('note').value || '');
    }, [modalParams.isOpen]);

    return (
        <section>
            <div className="modal__header">
                <h3 className="modal__header__title">Insurance Changes Note</h3>
            </div>
            <div className="modal__body">
                <div className="form__group">
                    <textarea
                        className="input input--med input--textarea"
                        placeholder="Enter Note"
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        rows={4}
                        autoFocus
                    />
                </div>
            </div>
            <div className="modal__footer">
                <BaasicButton
                    className="btn btn--med btn--light"
                    label="Cancel"
                    onClick={() => { modalParams.close(); }}
                />
                <BaasicButton
                    className="btn btn--med btn--primary"
                    label="Save"
                    onClick={() => { form.$('note').set('value', noteValue); modalParams.close(); }}
                />
            </div>
        </section>
    );
};

const InsuranceInfoNoteModalContent = ({ form, modalParams, onInsuranceInfoNoteChange }) => {
    const [noteValue, setNoteValue] = React.useState(form.$('insuranceInfoNote').value || '');

    React.useEffect(() => {
        setNoteValue(form.$('insuranceInfoNote').value || '');
    }, [modalParams.isOpen]);

    return (
        <section>
            <div className="modal__header">
                <h3 className="modal__header__title">Insurance Info Note</h3>
            </div>
            <div className="modal__body">
                <div className="form__group">
                    <textarea
                        className="input input--med input--textarea"
                        placeholder="Enter Insurance Info Note"
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        rows={4}
                        autoFocus
                    />
                </div>
            </div>
            <div className="modal__footer">
                <BaasicButton
                    className="btn btn--med btn--light"
                    label="Cancel"
                    onClick={() => modalParams.close()}
                />
                <BaasicButton
                    className="btn btn--med btn--primary"
                    label="Save"
                    onClick={() => {
                        form.$('insuranceInfoNote').set('value', noteValue);
                        modalParams.close();
                        onInsuranceInfoNoteChange();
                    }}
                />
            </div>
        </section>
    );
};

const InsuranceStatusNoteModalContent = ({ form, modalParams }) => {
    const [noteValue, setNoteValue] = React.useState(form.$('insuranceStatusNote').value || '');

    React.useEffect(() => {
        setNoteValue(form.$('insuranceStatusNote').value || '');
    }, [modalParams.isOpen]);

    return (
        <section>
            <div className="modal__header">
                <h3 className="modal__header__title">Eligibility Status Note</h3>
            </div>
            <div className="modal__body">
                <div className="form__group">
                    <textarea
                        className="input input--med input--textarea"
                        placeholder="Enter Eligibility Status Note"
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        rows={4}
                        autoFocus
                    />
                </div>
            </div>
            <div className="modal__footer">
                <BaasicButton
                    className="btn btn--med btn--light"
                    label="Cancel"
                    onClick={() => modalParams.close()}
                />
                <BaasicButton
                    className="btn btn--med btn--primary"
                    label="Save"
                    onClick={() => { form.$('insuranceStatusNote').set('value', noteValue); modalParams.close(); }}
                />
            </div>
        </section>
    );
};

const InsuranceInfoStep = observer(({ store }) => {
    const {
        form,
        insuranceStatusStore,
        primaryPolicies,
        secondaryPolicies,
        addPrimaryPolicy,
        addInactivePrimaryPolicy,
        addSecondaryPolicy,
        addInactiveSecondaryPolicy,
        removePrimaryPolicy,
        removeSecondaryPolicy,
        updatePolicyField,
        savePolicyEntry,
        editPolicyEntry,
        cancelPolicyEntry,
        insuranceQuestionModal,
        insuranceChangesNoteModal,
        insuranceInfoNoteModal,
        insuranceStatusNoteModal,
        deletePolicyModal,
        inactivatePolicyModal,
        policyErrors,
        onInsuranceInfoNoteChange,
    } = store;

    const hasActivePrimary = primaryPolicies.some(p => p.isActive);
    const hasInactivePrimary = primaryPolicies.some(p => !p.isActive);
    const hasActiveSecondary = secondaryPolicies.some(p => p.isActive);
    const hasInactiveSecondary = secondaryPolicies.some(p => !p.isActive);

    return (
        <div>
            <div className="u-flex--default u-flex--space-between u-mar--bottom--med">

                <div className='u-flex--default u-flex--gap--4'>
                    <h4 className="type--med type--wgt--medium">Insurance Info</h4>
                    {form.$('insuranceQuestion').value &&
                    <div className="u-flex u-flex--default u-flex--gap--2">
                        <div className="card--tertiary__header__divider"></div>
                        <Button className="btn btn--status--red" icon="u-icon u-icon--med-lrg u-icon--us-question-note--red" onClick={() => insuranceQuestionModal.open()} />
                    </div>
                    }
                </div>
                <div className="u-flex--deafault u-flex--gap--1">
                    <div className='btn--group is-visible'>
                        {!form.$('insuranceQuestion').value &&
                        <React.Fragment>
                            <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceQuestionTooltip"} place="bottom" />
                            <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-question-note-sh" onClick={() => insuranceQuestionModal.open()} data-tooltip-content="Insurance Question" data-tooltip-id={"nikoPatientInsuranceQuestionTooltip"} />
                        </React.Fragment>
                        }
                        <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceInfoNoteTooltip"} place="bottom" />
                        <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-note" onClick={() => insuranceInfoNoteModal.open()} data-tooltip-content="Insurance Info Note" data-tooltip-id={"nikoPatientInsuranceInfoNoteTooltip"} />

                        <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceChangesTooltip"} place="bottom" />
                        <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--danger-triangle--primary" onClick={() => insuranceChangesNoteModal.open()} data-tooltip-content="Insurance Changes Note" data-tooltip-id={"nikoPatientInsuranceChangesTooltip"} />
                    </div>
                </div>
            </div>
            {form.$('insuranceInfoNote').value && (
                <div className="c-message has-input c-message--note u-mar--bottom--sml">
                    <i className="u-icon u-icon--sml u-icon--add-note" />
                    <input
                        className="input input--sml input--message u-flex--grow--1 type--sml"
                        onClick={(e) => e.target.select()}
                        onChange={(e) => store.setInsuranceInfoNote(e.target.value)}
                        value={form.$('insuranceInfoNote').value || ''}
                    />
                </div>
            )}
            {form.$('note').value && (
                <div className="u-mar--bottom--sml c-message c-message--note--primary">
                    <div className={`has-input u-flex--default u-flex--gap--1 w--100`} >
                        Note: <p style={{ fontWeight: "normal" }}>{form.$('note').value}</p>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="form__group col col-lrg-6 u-mar--bottom--sml">
                    <BaasicFieldDropdown field={form.$('insuranceStatusId')} store={insuranceStatusStore} />
                </div>
                <div className="form__group col col-lrg-6 u-mar--bottom--sml">
                    <div className="u-flex--default u-flex--gap--1 u-flex--align-end">
                        <div className="u-flex--grow--1">
                            <DatePickerField field={form.$('effectiveDate')} />
                        </div>
                        {insuranceStatusStore.value && insuranceStatusStore.value.name !== "Eligible" && (
                            <div className='btn--group is-visible'>
                                <ReactTooltip positionStrategy="fixed" id={"nikoPatientInsuranceStatusNoteTooltip"} place="bottom" />
                                <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-note" onClick={() => insuranceStatusNoteModal.open()} data-tooltip-content="Eligibility Status Note" data-tooltip-id={"nikoPatientInsuranceStatusNoteTooltip"} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {insuranceStatusStore.value && insuranceStatusStore.value.name === "Eligible" &&
                <div className="message message--note is-round u-mar--bottom--med">
                    <i className="u-icon u-icon--med u-icon--warning-circle--peach"></i>
                    <div>
                        Primary Insurance must be added for eligible patients.
                    </div>
                </div>
            }
            <div className="u-mar--bottom--med">
                <h4 className="type--med type--color--text u-mar--bottom--med">Primary Insurance</h4>
                {primaryPolicies.filter(p => p.isActive).map((policy) => {
                    const index = primaryPolicies.indexOf(policy);
                    return (
                        <InsurancePolicyRow
                            key={policy.id}
                            policy={policy}
                            isPrimary={true}
                            isSwap={store.isPolicySwapped(policy)}
                            action={store.getPolicyAction(policy)}
                            onRemove={() => deletePolicyModal.open({ icon: "delete", title: "Delete Policy", additionalMessage: "Are you sure you want to delete this primary insurance policy?", action: () => { removePrimaryPolicy(index); deletePolicyModal.close(); } })}
                            onInactivate={() => inactivatePolicyModal.open({ type: "primary", index, policy })}
                            onCancel={() => cancelPolicyEntry("primary", index)}
                            onUpdateField={(field, value) => updatePolicyField("primary", index, field, value)}
                            onSave={() => savePolicyEntry("primary", index)}
                            onEdit={() => editPolicyEntry("primary", index)}
                            errors={policyErrors[policy.id] || {}}
                        />
                    );
                })}
                {!hasActivePrimary && (
                    <BaasicButton className="btn btn--base btn--secondary u-mar--top--med u-mar--bottom--med" label="+ Add Policy" onClick={addPrimaryPolicy} />
                )}
                {hasInactivePrimary && (
                    <h5 className="type--sml type--wgt--medium u-mar--bottom--sml u-mar--top--sml">Inactive Primary Insurance</h5>
                )}
                {primaryPolicies.filter(p => !p.isActive).map((policy) => {
                    const index = primaryPolicies.indexOf(policy);
                    return (
                        <InsurancePolicyRow
                            key={policy.id}
                            policy={policy}
                            isPrimary={true}
                            isSwap={store.isPolicySwapped(policy)}
                            action={store.getPolicyAction(policy)}
                            onRemove={() => deletePolicyModal.open({ icon: "delete", title: "Delete Policy", additionalMessage: "Are you sure you want to delete this primary insurance policy?", action: () => { removePrimaryPolicy(index); deletePolicyModal.close(); } })}
                            onInactivate={() => inactivatePolicyModal.open({ type: "primary", index, policy })}
                            onCancel={() => cancelPolicyEntry("primary", index)}
                            onUpdateField={(field, value) => updatePolicyField("primary", index, field, value)}
                            onSave={() => savePolicyEntry("primary", index)}
                            onEdit={() => editPolicyEntry("primary", index)}
                            errors={policyErrors[policy.id] || {}}
                        />
                    );
                })}
                {!hasInactivePrimary && (
                    <BaasicButton className="btn btn--base btn--card btn--card--light" label="+ Add Inactive Policy" onClick={addInactivePrimaryPolicy} />
                )}
            </div>

            <div className="u-mar--bottom--med">
                <h4 className="type--med type--color--text u-mar--bottom--med">Secondary Insurance</h4>
                {secondaryPolicies.filter(p => p.isActive).map((policy) => {
                    const index = secondaryPolicies.indexOf(policy);
                    return (
                        <InsurancePolicyRow
                            key={policy.id}
                            policy={policy}
                            isPrimary={false}
                            isSwap={store.isPolicySwapped(policy)}
                            action={store.getPolicyAction(policy)}
                            onRemove={() => deletePolicyModal.open({ icon: "delete", title: "Delete Policy", additionalMessage: "Are you sure you want to delete this secondary insurance policy?", action: () => { removeSecondaryPolicy(index); deletePolicyModal.close(); } })}
                            onInactivate={() => inactivatePolicyModal.open({ type: "secondary", index, policy })}
                            onCancel={() => cancelPolicyEntry("secondary", index)}
                            onUpdateField={(field, value) => updatePolicyField("secondary", index, field, value)}
                            onSave={() => savePolicyEntry("secondary", index)}
                            onEdit={() => editPolicyEntry("secondary", index)}
                            errors={policyErrors[policy.id] || {}}
                        />
                    );
                })}
                {!hasActiveSecondary && (
                    <BaasicButton className="btn btn--base btn--secondary u-mar--top--med u-mar--bottom--med" label="+ Add Policy" onClick={addSecondaryPolicy} />
                )}
                {hasInactiveSecondary && (
                    <h5 className="type--sml type--wgt--medium u-mar--bottom--sml u-mar--top--sml">Inactive Secondary Insurance</h5>
                )}
                {secondaryPolicies.filter(p => !p.isActive).map((policy) => {
                    const index = secondaryPolicies.indexOf(policy);
                    return (
                        <InsurancePolicyRow
                            key={policy.id}
                            policy={policy}
                            isPrimary={false}
                            isSwap={store.isPolicySwapped(policy)}
                            action={store.getPolicyAction(policy)}
                            onRemove={() => deletePolicyModal.open({ icon: "delete", title: "Delete Policy", additionalMessage: "Are you sure you want to delete this secondary insurance policy?", action: () => { removeSecondaryPolicy(index); deletePolicyModal.close(); } })}
                            onInactivate={() => inactivatePolicyModal.open({ type: "secondary", index, policy })}
                            onCancel={() => cancelPolicyEntry("secondary", index)}
                            onUpdateField={(field, value) => updatePolicyField("secondary", index, field, value)}
                            onSave={() => savePolicyEntry("secondary", index)}
                            onEdit={() => editPolicyEntry("secondary", index)}
                            errors={policyErrors[policy.id] || {}}
                        />
                    );
                })}
                {!hasInactiveSecondary && (
                    <BaasicButton className="btn btn--base btn--card btn--card--light" label="+ Add Inactive Policy" onClick={addInactiveSecondaryPolicy} />
                )}
            </div>

            <BaasicModal modalParams={insuranceQuestionModal} className="modal__content modal--med">
                <InsuranceQuestionModalContent form={form} modalParams={insuranceQuestionModal} />
            </BaasicModal>

            <BaasicModal modalParams={insuranceChangesNoteModal} className="modal__content modal--med">
                <InsuranceChangesNoteModalContent form={form} modalParams={insuranceChangesNoteModal} />
            </BaasicModal>

            <BaasicModal modalParams={insuranceInfoNoteModal} className="modal__content modal--med">
                <InsuranceInfoNoteModalContent form={form} modalParams={insuranceInfoNoteModal} onInsuranceInfoNoteChange={onInsuranceInfoNoteChange} />
            </BaasicModal>

            <BaasicModal modalParams={insuranceStatusNoteModal} className="modal__content modal--med">
                <InsuranceStatusNoteModalContent form={form} modalParams={insuranceStatusNoteModal} />
            </BaasicModal>

            <BaasicDraggableModal modalParams={deletePolicyModal} className="modal__content modal--med">
                <NikoFacilityOrderConfirmationModal icon="delete"/>
            </BaasicDraggableModal>

            <BaasicModal modalParams={inactivatePolicyModal} className="modal__content modal--med">
                <InactivatePolicyModalContent store={store} modalParams={inactivatePolicyModal} />
            </BaasicModal>
        </div>
    );
});

const TemplatesStep = observer(({ store }) => {
    const { templateInstances, addTemplate, removeTemplate, disabled, pageType } = store;

    return (
        <div>
            <h4 className="type--med type--wgt--medium u-mar--bottom--med">Templates</h4>
            {templateInstances.map((instance, index) => (
                <div key={instance.id} className="table--secondary__wrapper no-hover-line u-mar--bottom--med--condition is-niko has-top-padding">
                    <NikoNewPatientTemplateCreate
                        nikoTemplateCreateViewStore={instance.store}
                        disabled={disabled}
                        cssClass="niko--create__wrap"
                        tableClass="niko--create__table"
                        showDnsTag={pageType === 'USI' || pageType === 'CR'}
                        onRemove={templateInstances.length > 1 ? () => removeTemplate(index) : null}
                    />
                </div>
            ))}
            <BaasicButton
                className="btn btn--base btn--secondary u-mar--top--med"
                label="+ Add Template"
                onClick={addTemplate}
                disabled={disabled}
            />
        </div>
    );
});

const SummaryTemplateItemsTable = observer(({ items }) => {
    if (!items || items.length === 0) return null;

    return (
        <div className="table--tertiary__wrapper u-mar--top--sml">
            <table className="table table--tertiary niko--create__table is-fixed">
                <colgroup>
                    <col/>
                    <col width="200" />
                    <col width="90" />
                    <col />
                </colgroup>
                <thead>
                    <tr>
                        <th>Item Name</th>
                        <th>Item Id</th>
                        <th>QTY</th>
                        <th>NOTE</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={index}>
                            <td className="is-flex">
                                {item.product && item.product.description} <span className="table__icons-wrapper"> {item.product && !item.product.billableToMedicare && <i className="u-icon u-icon--sml u-icon--non-billable"></i>} {(item.backordered != null ? item.backordered : item.product && item.product.backordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</span>
                            </td>
                            <td>{item.product && item.product.itemId}</td>
                            <td>{item.quantity || 0}</td>
                            <td>{item.note}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});

const SummaryStep = observer(({ store }) => {
    const summaryData = store.getSummaryData();
    const { patientInfo, insuranceInfo, templates } = summaryData;

    return (
        <div>
            <h4 className="type--med type--wgt--medium u-mar--bottom--med">Summary</h4>

            <div className="u-mar--bottom--med">
                <h4 className="type--base type--wgt--medium u-mar--bottom--med">Patient Info:</h4>
                <div className='u-flex--default u-flex--gap--2 u-mar--bottom--med'>
                    <span>{patientInfo.firstName} {patientInfo.lastName}</span>
                    <div className="patient__group is-big">
                        <div className="u-flex--primary u-flex--no-wrap">
                            <i className="u-icon u-icon--med u-icon--patient-user u-mar--right--tny"></i>
                            <span>{patientInfo.gender}</span>
                        </div>
                        <span className="patient__group__divider">|</span>
                        <div className="u-flex--primary u-flex--no-wrap">
                            <i className="u-icon u-icon--med u-icon--patient-camera u-mar--right--tny"></i>
                            <span>{moment(patientInfo.dateOfBirth).format("MM/DD/YYYY")}</span>
                        </div>
                        <span className="patient__group__divider">|</span>
                        <div className="u-flex--primary u-flex--no-wrap">
                            <i className="u-icon u-icon--med u-icon--patient-ssn u-mar--right--tny"></i>
                            <span>{patientInfo.ssn}</span>
                        </div>
                        <span className="patient__group__divider">|</span>
                        <div className="u-flex--primary u-flex--no-wrap">
                            <i className="u-icon u-icon--med u-icon--new-facility--primary u-mar--right--tny"></i>
                            <span>{patientInfo.facility && patientInfo.facility.name}</span>
                        </div>
                </div>
                </div>
            </div>

            {!store.isInsuranceStepSkipped && (
                <div className="u-mar--bottom--med">
                    <h4 className="type--base type--wgt--medium u-mar--bottom--med">Insurance Info:</h4>
                    <div className="card--tertiary__flex u-mar--bottom--med">
                        {insuranceInfo.insuranceStatus && (
                            <div className="status--sqare__wrapper u-mar--bottom--tny u-flex--default u-flex--gap--1">
                                <BaasicButton
                                    type='button'
                                    className="btn btn--base-plus btn--light btn--uppercase u-mar--right--sml no-click"
                                    label={insuranceInfo.insuranceStatus}
                                />
                                {store.parConfirmed && store.templatesNeedingPar.some(t => t.isPriorAuthNeeded) && (
                                    <BaasicButton
                                        type='button'
                                        className="btn btn--base-plus btn--light is-grey no-click"
                                        label={`PAR ${store.templatesNeedingPar.filter(t => t.isPriorAuthNeeded).length}/${store.templatesNeedingPar.length}`}
                                    />
                                )}
                                {insuranceInfo.insuranceStatusNote && (
                                    <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-note no-click" disabled />
                                )}
                            </div>
                        )}
                        <div className="card--tertiary__data">
                            {insuranceInfo.primaryPolicies.length > 0 && (
                                <div>
                                    {insuranceInfo.primaryPolicies.map((p, i) => (
                                        <div key={i} className="card--tertiary__data__item">
                                            <strong className='type--wgt--semibold'>{p.isActive ? 'Primary:': 'Inactive:'}</strong>
                                            <span className='type--color--primary type--wgt--semibold' key={i}> {p.insuranceName || 'N/A'}</span><span> {p.policyNo}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {insuranceInfo.secondaryPolicies.length > 0 && (
                                <React.Fragment>
                                    <div>
                                        {insuranceInfo.secondaryPolicies.map((p, i) => (
                                            <div key={i} className="card--tertiary__data__item">
                                                <strong className='type--wgt--semibold'>{p.isActive ? 'Secondary:': 'Inactive:'}</strong>
                                                <span className='type--color--primary type--wgt--semibold' key={i}> {p.insuranceName || 'N/A'}</span><span> {p.policyNo}{!p.isActive && ' (Inactive)'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </React.Fragment>
                            )}
                            {insuranceInfo.insuranceQuestion && (
                                <React.Fragment>
                                    <div className="card--tertiary__header__divider"></div>
                                    <div card--tertiary__data__item>
                                        <Button className="btn btn--status--red" icon="u-icon u-icon--med-lrg u-icon--us-question-note--red u-mar--bottom--tny" disabled />
                                    </div>
                                </React.Fragment>
                            )}

                        </div>
                    </div>
                    {insuranceInfo.insuranceInfoNote && (
                        <div className="c-message c-message--sml has-input c-message--note u-mar--bottom--sml font-regular">
                            <i className="u-icon u-icon--sml u-icon--add-note" />
                            <span className="type--sml u-flex--grow--1 u-mar--left--sml"> {insuranceInfo.insuranceInfoNote}</span>
                        </div>
                    )}
                    {insuranceInfo.note && (
                        <div className="u-mar--bottom--sml c-message c-message--sml c-message--note--primary font-regular">
                            <div className={`has-input u-flex--default u-flex--gap--1 w--100`} >
                                Note: <p style={{ fontWeight: "normal" }}>{insuranceInfo.note}</p>
                            </div>
                        </div>
                    )}

                    <BaasicModal modalParams={store.eligibilityStatusNoteModal} className="modal__content modal--med">
                        <InsuranceStatusNoteModalContent form={store.form} modalParams={store.eligibilityStatusNoteModal} />
                    </BaasicModal>
                </div>
            )}

            {templates.length > 0 && (
                <div className="u-mar--bottom--med">
                    <h4 className="type--base type--wgt--medium u-mar--bottom--med">Templates:</h4>
                    {templates.map((template, key) => (
                        <div key={key} className="table--secondary__wrapper no-hover-line u-mar--bottom--med--condition is-niko has-top-padding">
                            <div className="u-flex--default u-flex--gap--1 u-flex--align-center">
                                {template.subCategory && (
                                    <p className="type--med type--uppercase type--wgt--regular u-mar--top--tny">{template.subCategory}</p>
                                )}
                                {template.isSystemDns && (
                                    <div className="u-mar--left--xsml btn--disc__wrapper is-fr">Do not supply</div>
                                )}
                                {template.isUserDns && (
                                    <div className="u-mar--left--xsml btn--disc__wrapper is-fr">Do not supply - FR</div>
                                )}
                                {template.missingProductNote && (
                                    <Button className="btn btn--status--red u-mar--left--xsml" icon="u-icon u-icon--med-lrg u-icon--missing-product no-click" disabled />
                                )}
                            </div>
                            {template.note && (
                                <div className="u-mar--top--tny u-mar--bottom--sml c-message c-message--sml font-regular has-input u-flex--default w--100 c-message--note--primary">
                                    <i className="u-icon u-icon--sml u-icon--danger-triangle" />
                                    <span className="type--sml u-flex--grow--1 u-mar--left--sml">{template.note}</span>
                                </div>
                            )}
                            <SummaryTemplateItemsTable items={template.items} />
                            <SummaryTemplateItemsTable items={template.trachItems} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

const ParConfirmPopup = ({ store }) => {
    const { templatesNeedingPar, confirmPar } = store;
    const [localPar, setLocalPar] = React.useState([]);

    React.useEffect(() => {
        setLocalPar(templatesNeedingPar.map(t => t.isPriorAuthNeeded || false));
    }, [store.parConfirmModal.isOpen]);

    var toggleLocal = function (index) {
        var updated = localPar.slice();
        updated[index] = !updated[index];
        setLocalPar(updated);
    };

    var handleConfirm = function () {
        localPar.forEach(function (val, index) {
            if (templatesNeedingPar[index]) {
                templatesNeedingPar[index].isPriorAuthNeeded = val;
            }
        });
        confirmPar();
    };

    return (
        <section>
            <div className="modal__header"></div>
            <div className="modal__body">
                <p className="type--sml u-mar--bottom--sml">Check off if PAR is required for any of the templates below.</p>
                {templatesNeedingPar.map((template, index) => {
                    return <div key={index} className="u-mar--bottom--tny u-display--flex u-display--flex--ai--center">
                            <input
                                id={`par-template-${index}`}
                                className='input--check'
                                type="checkbox"
                                checked={localPar[index] || false}
                                onChange={() => toggleLocal(index)}
                            />
                            <label htmlFor={`par-template-${index}`}>{template.subCategoryName || `Template ${index + 1}`}</label>
                    </div>;
                })}
            </div>
            <div className="modal__footer is-right">
                <BaasicButton className="btn btn--med btn--primary" label="Confirm" onClick={handleConfirm} />
            </div>
        </section>
    );
};

function NikoCreateNewPatientModalTemplate({ nikoCreateNewPatientModalStore }) {
    const {
        stepperStore,
        disabled,
        goToNextStep,
        goToPreviousStep,
        goToStep,
        isPatientInfoStep,
        isInsuranceInfoStep,
        isTemplatesStep,
        isSummaryStep,
        showExistingPatientList,
        checkingExisting,
        parConfirmModal,
        createResource,
        isStep1Valid,
    } = nikoCreateNewPatientModalStore;

    const { currentStep, items } = stepperStore;

    return (
        <section>
            <div className="modal__header">
                <h3 className="modal__header__title">Create New Patient</h3>
            </div>

            <div className="modal__body modal__body--flex" >
                <div className="modal__body__aside">
                    <Stepper
                        value={currentStep}
                        items={items.slice()}
                        orientation="vertical"
                        className={`stepper-vertical${isSummaryStep ? ' is-navigable' : ''}`}
                        onChange={(e) => goToStep(e.value)}
                    />
                </div>

                <div className='modal__body--step-container'>
                    {isPatientInfoStep && (
                        <PatientInfoStep store={nikoCreateNewPatientModalStore} />
                    )}
                    {isInsuranceInfoStep && (
                        <InsuranceInfoStep store={nikoCreateNewPatientModalStore} />
                    )}
                    {isTemplatesStep && (
                        <TemplatesStep store={nikoCreateNewPatientModalStore} />
                    )}
                    {isSummaryStep && (
                        <SummaryStep store={nikoCreateNewPatientModalStore} />
                    )}
                </div>
            </div>

            <div className="modal__footer is-right">
                {currentStep > 1 && (
                    <BaasicButton
                        className="btn btn--med btn--light is-wider"
                        label="Previous"
                        icon="u-icon u-icon--intermed u-icon--arrow-right--next2 u-rotate--180"
                        onClick={goToPreviousStep}
                        disabled={disabled}
                    />
                )}
                {!isSummaryStep && (
                    <BaasicButton
                        className="btn btn--med btn--primary btn--invert is-wider"
                        label="Next"
                        icon="u-icon u-icon--intermed u-icon--arrow-right--next"
                        iconPosition="right"
                        onClick={goToNextStep}
                        disabled={disabled || checkingExisting || (isPatientInfoStep && showExistingPatientList && !nikoCreateNewPatientModalStore.existingNikoPatientId) || (isPatientInfoStep && !isStep1Valid) || (isPatientInfoStep && !nikoCreateNewPatientModalStore.form.$('dateOfBirth').isValid) || (isInsuranceInfoStep && !nikoCreateNewPatientModalStore.insuranceStatusStore.value) || (isInsuranceInfoStep && nikoCreateNewPatientModalStore.insuranceStatusStore.value && nikoCreateNewPatientModalStore.insuranceStatusStore.value.name === "Eligible" && !nikoCreateNewPatientModalStore.primaryPolicies.some(p => p.isActive && p.insuranceId)) || (isTemplatesStep && !nikoCreateNewPatientModalStore.templateInstances.some(inst => inst.store.subCategoryStore.value))}
                    />
                )}
                {isSummaryStep && (
                    <BaasicButton
                        className="btn btn--med btn--primary"
                        label="Complete"
                        onClick={createResource}
                        disabled={disabled}
                        rotate
                        icon={disabled ? "u-icon u-icon--xmed u-icon--sync u-rotate--login" : "u-display--none"}
                    />
                )}
            </div>

            <BaasicModal modalParams={parConfirmModal} className="modal__content modal--med">
                <ParConfirmPopup store={nikoCreateNewPatientModalStore} />
            </BaasicModal>
        </section>
    );
}

NikoCreateNewPatientModalTemplate.propTypes = {
    nikoCreateNewPatientModalStore: PropTypes.object,
    t: PropTypes.func,
};

ExistingPatientList.propTypes = {
    store: PropTypes.object.isRequired,
};

PatientInfoStep.propTypes = {
    store: PropTypes.object.isRequired,
};

InsurancePolicyRow.propTypes = {
    policy: PropTypes.object.isRequired,
    isPrimary: PropTypes.bool.isRequired,
    isSwap: PropTypes.bool,
    onRemove: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onUpdateField: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
};

InsuranceQuestionModalContent.propTypes = {
    form: PropTypes.object.isRequired,
    modalParams: PropTypes.object.isRequired,
};

InsuranceChangesNoteModalContent.propTypes = {
    form: PropTypes.object.isRequired,
    modalParams: PropTypes.object.isRequired,
};

InsuranceInfoNoteModalContent.propTypes = {
    form: PropTypes.object.isRequired,
    modalParams: PropTypes.object.isRequired,
    onInsuranceInfoNoteChange: PropTypes.func.isRequired,
};

InsuranceStatusNoteModalContent.propTypes = {
    form: PropTypes.object.isRequired,
    modalParams: PropTypes.object.isRequired,
};

InsuranceInfoStep.propTypes = {
    store: PropTypes.object.isRequired,
};

TemplatesStep.propTypes = {
    store: PropTypes.object.isRequired,
};

SummaryTemplateItemsTable.propTypes = {
    items: PropTypes.array,
};

SummaryStep.propTypes = {
    store: PropTypes.object.isRequired,
};

ParConfirmPopup.propTypes = {
    store: PropTypes.object.isRequired,
};

export default defaultTemplate(NikoCreateNewPatientModalTemplate);
